import React, { useState, useEffect, useCallback } from 'react';
import { driverAPI, busTripAPI } from '../services/api';

// Rendered outside <Router>; use window.location for navigation.
const goTo = (path) => { window.location.href = path; };

// Which trip phase are we in? Derived from EVENT_TYPES (backend).
const PHASE_LABELS = {
  pickup: '🚏 Pickup',
  school: '🏫 Reached School',
  drop:   '🏠 Drop-off',
  done:   '✅ Trip Complete',
};

export default function DriverMode() {
  const [session] = useState(() => driverAPI.getSession());
  const [tripType, setTripType] = useState(() => (new Date().getHours() < 12 ? 'morning' : 'evening'));
  const [loading, setLoading]   = useState(true);
  const [data, setData]         = useState(null);
  const [err, setErr]           = useState('');
  const [busyEvent, setBusyEvent] = useState(null);
  const [activeStopId, setActiveStopId] = useState(null);
  const [confirm, setConfirm]   = useState(null);

  useEffect(() => {
    if (!session?.token) { goTo('/driver-login'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripType]);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data: r } = await busTripAPI.today({ trip_type: tripType });
      if (!r?.success) { setErr(r?.message || 'Failed to load trip'); setLoading(false); return; }
      setData(r.data);
      // Default active stop = first stop that has no arrived event yet
      const stops = r.data.stops || [];
      const evs = r.data.recent_events || [];
      const arrivedIds = new Set(evs.filter(e => e.event_type === (tripType === 'morning' ? 'arrived_pickup' : 'arrived_drop')).map(e => e.stop_id));
      const firstPending = stops.find(s => !arrivedIds.has(s.stop_id));
      setActiveStopId(firstPending?.stop_id || stops[0]?.stop_id || null);
    } catch (e) {
      if (e?.response?.status === 401) { driverAPI.logout(); goTo('/driver-login'); return; }
      setErr(e?.response?.data?.message || 'Failed to load');
    }
    setLoading(false);
  }, [tripType]);

  const fireEvent = async (event_type, extra = {}, opts = {}) => {
    if (!data?.trip?.trip_id) return;
    setBusyEvent(event_type + JSON.stringify(extra));
    try {
      const { data: r } = await busTripAPI.event(data.trip.trip_id, {
        event_type, send_whatsapp: true, ...extra,
      });
      if (!r?.success) { alert(r?.message || 'Failed'); }
      else if (!opts.quiet) {
        const n = r?.data?.notified || 0;
        if (n > 0) toast(`📱 Notified ${n} parent${n === 1 ? '' : 's'}`);
      }
      await load();
    } catch (e) {
      alert(e?.response?.data?.message || 'Failed');
    }
    setBusyEvent(null);
  };

  const endTrip = async () => {
    if (!window.confirm('End this trip? No further events can be logged.')) return;
    try {
      await busTripAPI.end(data.trip.trip_id);
      await load();
    } catch (e) { alert(e?.response?.data?.message || 'Failed'); }
  };

  const logout = () => {
    if (!window.confirm('Log out of driver mode?')) return;
    driverAPI.logout();
    goTo('/driver-login');
  };

  if (loading) return <FullMsg>Loading today's trip…</FullMsg>;
  if (err)     return (
    <FullMsg error>
      <div style={{ maxWidth: 400 }}>{err}</div>
      <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'center' }}>
        <button style={{ ...s.smBtn, background: '#1976d2', color: '#fff' }} onClick={load}>🔄 Retry</button>
        <button style={{ ...s.smBtn, background: '#455a64', color: '#fff' }} onClick={() => { driverAPI.logout(); goTo('/driver-login'); }}>🚪 Logout</button>
      </div>
    </FullMsg>
  );
  if (!data)   return <FullMsg>No data</FullMsg>;

  const { trip, route, stops = [], unassigned_students = [], recent_events = [] } = data;
  const isMorning = trip.trip_type === 'morning';
  const arrivedEv  = isMorning ? 'arrived_pickup'  : 'arrived_drop';
  const departedEv = isMorning ? 'departed_pickup' : null; // only morning
  const boardEv    = isMorning ? 'child_boarded'   : 'child_alighted';
  const absentEv   = isMorning ? 'child_absent'    : null;

  const doneStopIds  = new Set(recent_events.filter(e => e.event_type === arrivedEv).map(e => e.stop_id));
  const leftStopIds  = new Set(recent_events.filter(e => e.event_type === departedEv).map(e => e.stop_id));
  const reachedSchool = recent_events.some(e => e.event_type === 'reached_school');
  const startedReturn = recent_events.some(e => e.event_type === 'started_return');

  const activeStop = stops.find(s => s.stop_id === activeStopId) || stops[0];
  const tripDone = trip.status === 'completed';

  return (
    <div style={s.page}>
      {/* HEADER */}
      <header style={s.header}>
        <div>
          <div style={s.hTitle}>🚌 {session.bus_number || route?.bus_number || 'Bus'}</div>
          <div style={s.hSub}>{session.driver_name} • {route?.route_name || '(no route)'}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={s.iconBtn} onClick={load} title="Refresh">🔄</button>
          <button style={s.iconBtn} onClick={logout} title="Logout">🚪</button>
        </div>
      </header>

      {/* TRIP TYPE SWITCH */}
      <div style={s.tripSwitch}>
        <button
          style={{ ...s.tripBtn, ...(tripType === 'morning' ? s.tripBtnActive : {}) }}
          onClick={() => setTripType('morning')}
          disabled={busyEvent !== null}
        >🌅 Morning (Pickup)</button>
        <button
          style={{ ...s.tripBtn, ...(tripType === 'evening' ? s.tripBtnActive : {}) }}
          onClick={() => setTripType('evening')}
          disabled={busyEvent !== null}
        >🌆 Evening (Drop)</button>
      </div>

      {/* TRIP STATUS */}
      <div style={s.statusBar}>
        <span>{tripDone ? '✅ Trip completed' : '🟢 In progress'}</span>
        <span>Stops: {doneStopIds.size} / {stops.length}</span>
      </div>

      {/* IN MORNING TRIPS: reached school panel */}
      {isMorning && !tripDone && stops.length > 0 && doneStopIds.size === stops.length && !reachedSchool && (
        <BigPanel color="#2e7d32" onClick={() => fireEvent('reached_school')}
          disabled={busyEvent !== null}>
          🏫 Reached School — Notify Parents
        </BigPanel>
      )}
      {isMorning && reachedSchool && !tripDone && (
        <BigPanel color="#455a64" onClick={endTrip}>🏁 End Trip</BigPanel>
      )}

      {/* IN EVENING TRIPS: start return before drops */}
      {!isMorning && !tripDone && !startedReturn && (
        <BigPanel color="#1976d2" onClick={() => fireEvent('started_return')}>
          🌆 Start Return Trip — Notify Parents
        </BigPanel>
      )}
      {!isMorning && startedReturn && stops.length > 0 && doneStopIds.size === stops.length && !tripDone && (
        <BigPanel color="#455a64" onClick={endTrip}>🏁 End Trip</BigPanel>
      )}

      {/* STOPS LIST */}
      <div style={s.stopsWrap}>
        {stops.length === 0 && <div style={s.info}>No stops defined for this route yet.</div>}

        {stops.map((stop) => {
          const isActive  = stop.stop_id === activeStop?.stop_id;
          const arrived   = doneStopIds.has(stop.stop_id);
          const departed  = leftStopIds.has(stop.stop_id);
          const hidden    = !isMorning && !startedReturn; // in evening, keep drops hidden until "started return"
          if (hidden) return null;

          return (
            <div key={stop.stop_id} style={{
              ...s.stopCard,
              ...(isActive ? s.stopCardActive : {}),
              opacity: arrived && departed ? 0.7 : 1,
            }} onClick={() => setActiveStopId(stop.stop_id)}>
              <div style={s.stopHead}>
                <div>
                  <div style={s.stopName}>
                    #{stop.sequence_no} · {stop.stop_name}
                    {arrived && ' ✅'}
                  </div>
                  <div style={s.stopMeta}>
                    {isMorning && stop.pickup_time ? `Pickup: ${fmtT(stop.pickup_time)}` : ''}
                    {!isMorning && stop.drop_time  ? `Drop: ${fmtT(stop.drop_time)}` : ''}
                    {' • '}{stop.students?.length || 0} student{stop.students?.length === 1 ? '' : 's'}
                  </div>
                </div>
              </div>

              {isActive && !tripDone && (
                <div style={s.stopActions}>
                  {/* Arrived button */}
                  {!arrived && (
                    <BigBtn color="#1976d2" onClick={(e) => { e.stopPropagation(); fireEvent(arrivedEv, { stop_id: stop.stop_id }); }}
                      disabled={busyEvent !== null}>
                      🚏 {isMorning ? 'Arrived at Pickup' : 'Arrived at Drop'} — Notify Parents
                    </BigBtn>
                  )}

                  {/* Per-student buttons */}
                  {arrived && (stop.students || []).map((st) => {
                    const already = isMorning
                      ? (st.boarding_status === 'boarded' || st.boarding_status === 'absent')
                      : (st.boarding_status === 'alighted');
                    return (
                      <div key={st.student_id} style={s.studentRow}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700 }}>{st.student_name}</div>
                          <div style={s.stMeta}>
                            {st.class}
                            {st.boarding_status && <span style={{
                              marginLeft: 8,
                              padding: '2px 8px',
                              borderRadius: 6,
                              background: st.boarding_status === 'absent' ? '#ffebee' : '#e8f5e9',
                              color:      st.boarding_status === 'absent' ? '#c62828' : '#2e7d32',
                              fontSize: 12, fontWeight: 700,
                            }}>{st.boarding_status.toUpperCase()}</span>}
                          </div>
                        </div>
                        {!already && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              style={{ ...s.smBtn, background: '#2e7d32', color: '#fff' }}
                              onClick={(e) => { e.stopPropagation(); fireEvent(boardEv, { stop_id: stop.stop_id, student_id: st.student_id }); }}
                              disabled={busyEvent !== null}
                            >{isMorning ? '✅ Boarded' : '✅ Alighted'}</button>
                            {isMorning && (
                              <button
                                style={{ ...s.smBtn, background: '#c62828', color: '#fff' }}
                                onClick={(e) => { e.stopPropagation(); fireEvent(absentEv, { stop_id: stop.stop_id, student_id: st.student_id }); }}
                                disabled={busyEvent !== null}
                              >❌ Absent</button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Departed button (morning only) */}
                  {isMorning && arrived && !departed && (
                    <BigBtn color="#455a64" onClick={(e) => { e.stopPropagation(); fireEvent(departedEv, { stop_id: stop.stop_id }); advanceStop(stop, stops, setActiveStopId); }}
                      disabled={busyEvent !== null}>
                      🚌 Departed from Stop — Notify Parents
                    </BigBtn>
                  )}
                  {!isMorning && arrived && (
                    <BigBtn color="#455a64" onClick={(e) => { e.stopPropagation(); advanceStop(stop, stops, setActiveStopId); }}>
                      ➡️ Next Stop
                    </BigBtn>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Unassigned students (no bus_stop_id) */}
      {unassigned_students.length > 0 && (
        <div style={s.unassigned}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠️ Students on route without a stop:</div>
          {unassigned_students.map(u => <div key={u.student_id} style={{ fontSize: 13 }}>• {u.student_name} ({u.class})</div>)}
        </div>
      )}

      {/* Toast holder */}
      <div id="__toast" style={s.toastWrap} />

      {/* Bottom padding so last content isn't hidden behind mobile URL bar */}
      <div style={{ height: 60 }} />
    </div>
  );
}

// Move to next stop
function advanceStop(cur, all, setActive) {
  const idx = all.findIndex(x => x.stop_id === cur.stop_id);
  const next = all[idx + 1];
  if (next) setActive(next.stop_id);
}

function fmtT(t) {
  if (!t) return '';
  const s = String(t);
  const m = s.match(/(\d\d):(\d\d)/);
  if (!m) return s;
  let h = parseInt(m[1], 10);
  const min = m[2];
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${min} ${ampm}`;
}

function toast(msg) {
  const el = document.createElement('div');
  el.textContent = msg;
  el.style.cssText = 'padding:12px 18px;background:#323232;color:#fff;border-radius:8px;margin-top:8px;box-shadow:0 4px 12px rgba(0,0,0,0.3);font-size:14px;';
  const wrap = document.getElementById('__toast');
  if (wrap) { wrap.appendChild(el); setTimeout(() => el.remove(), 3500); }
}

function BigBtn({ children, color, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      width: '100%', padding: '18px', margin: '6px 0',
      background: color, color: '#fff', border: 'none', borderRadius: 12,
      fontSize: 17, fontWeight: 700, cursor: 'pointer',
      opacity: disabled ? 0.6 : 1,
    }}>{children}</button>
  );
}
function BigPanel({ children, color, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      width: '100%', padding: '22px', margin: '12px 0',
      background: color, color: '#fff', border: 'none', borderRadius: 14,
      fontSize: 18, fontWeight: 800, cursor: 'pointer', boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
      opacity: disabled ? 0.6 : 1,
    }}>{children}</button>
  );
}
function FullMsg({ children, error }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, background: error ? '#fee' : '#f5f5f5', color: error ? '#b00' : '#333',
      fontFamily: 'system-ui,sans-serif', textAlign: 'center' }}>
      <div>{children}</div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh', background: '#f5f7fa',
    fontFamily: 'system-ui,-apple-system,sans-serif',
    paddingBottom: 40, color: '#222',
  },
  header: {
    background: '#0b3d91', color: '#fff',
    padding: '14px 16px', display: 'flex',
    justifyContent: 'space-between', alignItems: 'center',
    position: 'sticky', top: 0, zIndex: 10,
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  },
  hTitle: { fontSize: 20, fontWeight: 800 },
  hSub:   { fontSize: 13, opacity: 0.85 },
  iconBtn: {
    background: 'rgba(255,255,255,0.15)', color: '#fff',
    border: 'none', borderRadius: 8, width: 40, height: 40,
    fontSize: 18, cursor: 'pointer',
  },
  tripSwitch: { display: 'flex', gap: 8, padding: 12 },
  tripBtn: {
    flex: 1, padding: '12px', background: '#fff',
    border: '2px solid #e0e0e0', borderRadius: 10,
    fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#555',
  },
  tripBtnActive: { background: '#0b3d91', color: '#fff', borderColor: '#0b3d91' },
  statusBar: {
    display: 'flex', justifyContent: 'space-between',
    padding: '8px 16px', background: '#e3f2fd', color: '#0b3d91',
    fontSize: 13, fontWeight: 600,
  },
  stopsWrap: { padding: '4px 12px' },
  stopCard: {
    background: '#fff', margin: '10px 0', padding: 14,
    borderRadius: 12, boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
    borderLeft: '5px solid #cfd8dc', cursor: 'pointer',
  },
  stopCardActive: { borderLeftColor: '#1976d2', boxShadow: '0 4px 14px rgba(25,118,210,0.25)' },
  stopHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  stopName: { fontWeight: 800, fontSize: 16 },
  stopMeta: { fontSize: 13, color: '#666', marginTop: 2 },
  stopActions: { marginTop: 10 },
  studentRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 12px', margin: '6px 0',
    background: '#f8f9fb', borderRadius: 8,
  },
  stMeta: { fontSize: 12, color: '#666', marginTop: 2 },
  smBtn: {
    padding: '10px 14px', border: 'none', borderRadius: 8,
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
    background: '#eee', color: '#333',
  },
  info: { padding: 16, textAlign: 'center', color: '#666' },
  unassigned: {
    margin: '10px 12px', padding: 12,
    background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: 8,
    fontSize: 13, color: '#e65100',
  },
  toastWrap: {
    position: 'fixed', bottom: 20, left: '50%',
    transform: 'translateX(-50%)', zIndex: 999,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    pointerEvents: 'none',
  },
};
